import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, RefreshCw, Check } from 'lucide-react';
import { visiteAPI } from '../services/api';
import { useAuthStore } from '../store/useStore';
import { notificaErrore, messaggioDa } from '../utils/notifiche';
import { dataOraIta } from '../utils/oraItaliana';
import '../styles/ApertureRiservate.css';

/**
 * ApertureRiservate — chi ha aperto una procedura riservata.
 *
 * PERCHE' QUESTA SCHERMATA NON C'ERA, E PERCHE' DOVEVA ESSERCI
 * Il controllo sulla riservatezza delle procedure estetiche si regge su
 * una promessa: chi apre senza essere l'esecutore lascia una traccia, e
 * quella traccia qualcuno la vede. Fino al 23 settembre 2026 la prima
 * meta' era vera e la seconda no. Le aperture finivano in
 * `accessi_riservati`, la rotta per leggerle esisteva, era pure
 * dichiarata in `services/api.js` - e **nessun componente la chiamava**.
 * Nel registro degli accessi generale non comparivano di proposito
 * (sono gia' annotate qui, e la stessa apertura in due registri fa due
 * conteggi). Risultato: un medico senior che apriva da se' non lo
 * sapeva nessuno.
 *
 * CHI VEDE COSA: il medico vede le aperture sulle PROPRIE procedure -
 * e' lui la persona a cui quella traccia serve; l'amministrazione le
 * vede tutte. Lo decide il server (`apertureRiservate`), non questa
 * pagina.
 *
 * NESSUN AVVISO IN TEMPO REALE per le aperture non urgenti (decisione
 * di Alex, 23 settembre 2026): si trovano qui. L'urgenza continua ad
 * avvisare subito, com'era.
 *
 * COSA NON SI SCRIVE: della procedura si mostra la data e la sintesi,
 * che il medico esecutore gia' conosce - e' la sua. Non si aggiunge
 * niente che allarghi la platea di chi legge un dato clinico.
 */

const ApertureRiservate = () => {
    const utente = useAuthStore((s) => s.user);
    const [aperture, setAperture] = useState([]);
    const [daVedere, setDaVedere] = useState(0);
    const [inCorso, setInCorso] = useState(false);

    const carica = useCallback(async () => {
        try {
            const r = await visiteAPI.aperture();
            setAperture(r.data.data.aperture || []);
            setDaVedere(r.data.data.da_vedere || 0);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a leggere le aperture.'));
        }
    }, []);

    useEffect(() => { carica(); }, [carica]);

    const segnaViste = async () => {
        try {
            setInCorso(true);
            await visiteAPI.apertureViste();
            await carica();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a segnarle come viste.'));
        } finally { setInCorso(false); }
    };

    const tutte = utente?.ruolo === 'admin';

    return (
        <div className="apr-wrap">
            <div className="apr-testata">
                <h1><ShieldAlert size={22} /> Aperture delle procedure riservate</h1>
                <button className="apr-btn" onClick={carica}>
                    <RefreshCw size={15} /> Aggiorna
                </button>
                {daVedere > 0 && (
                    <button className="apr-btn apr-btn--principale" onClick={segnaViste} disabled={inCorso}>
                        <Check size={15} /> Ho visto {daVedere === 1 ? 'l’apertura' : `le ${daVedere} aperture`}
                    </button>
                )}
            </div>

            <p className="apr-nota">
                {tutte
                    ? 'Tutte le aperture di procedure riservate fatte da chi non le ha eseguite.'
                    : 'Chi ha aperto le procedure che hai eseguito tu.'}
                {' '}Una procedura di medicina estetica riservata la vede il medico che l’ha
                eseguita; chiunque altro deve <b>aprirla</b>, e l’apertura finisce qui con il
                nome, il momento e il motivo. <b>Non arriva nessun avviso</b> per le aperture
                non urgenti: si trovano qui. Un’apertura dichiarata <b>urgente</b> avvisa
                subito, come prima.
            </p>

            {aperture.length === 0 ? (
                <p className="apr-vuoto">
                    {tutte
                        ? 'Nessuno ha aperto procedure riservate.'
                        : 'Nessuno ha aperto le tue procedure riservate.'}
                </p>
            ) : (
                <div className="rm-scorre">
                <table className="apr-tabella">
                    <thead>
                        <tr>
                            <th>Quando</th><th>Chi</th><th>Paziente</th>
                            <th>Procedura</th><th>Motivo</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {aperture.map((a) => (
                            <tr key={a.id} className={a.notificato_il ? '' : 'apr-nuova'}>
                                <td>{dataOraIta(a.created_at)}</td>
                                <td>
                                    <b>{a.utente_nome || '—'}</b>
                                    {a.urgenza && <span className="apr-urgenza">urgenza</span>}
                                    {!a.urgenza && a.richiesta_id && (
                                        <span className="apr-sub">con autorizzazione</span>
                                    )}
                                    {!a.urgenza && !a.richiesta_id && (
                                        <span className="apr-sub">aperta da sé</span>
                                    )}
                                </td>
                                <td>
                                    {a.paziente_cognome ? `${a.paziente_cognome} ${a.paziente_nome}` : '—'}
                                    {a.id_paziente && <span className="apr-sub">{a.id_paziente}</span>}
                                </td>
                                <td>
                                    {a.data_visita ? new Date(a.data_visita).toLocaleDateString('it-IT',
                                        { timeZone: 'Europe/Rome' }) : '—'}
                                    {a.sintesi_clinica && <span className="apr-sub">{a.sintesi_clinica}</span>}
                                </td>
                                <td className="apr-motivo">{a.motivo || '—'}</td>
                                <td>{a.notificato_il ? '' : <span className="apr-pallino" title="non ancora vista" />}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            )}
        </div>
    );
};

export default ApertureRiservate;
